import apiClient from '../api/apiClient';

const adminService = {
  
  // Get users by role (distributors, retailers, fos)
  getUsers: async (params = {}) => {
    try {
      const {
        role = 'distributor',
        page = 1,
        page_size = 20,
        search = '',
        region = '',
        district = '',
        city = ''
      } = params;

      const queryParams = new URLSearchParams({
        role,
        page: page.toString(),
        page_size: page_size.toString()
      });

      // Add optional search and filter parameters
      if (search) queryParams.append('search', search);
      if (region) queryParams.append('region', region);
      if (district) queryParams.append('district', district);
      if (city) queryParams.append('city', city);

      const response = await apiClient.get(`/api/admin/users/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            results: response.data.results,
            totalPages: Math.ceil(response.data.count / page_size),
            currentPage: page,
            pageSize: page_size
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch users'
      };
    }
  },

  // Get user by ID
  getUserById: async (userId) => {
    try {
      const response = await apiClient.get(`/api/admin/users/${userId}/`);
      
      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'User not found' };
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch user'
      };
    }
  },

  // Create new user
  createUser: async (userData) => {
    try {
      const response = await apiClient.post('/api/admin/users/', userData);
      
      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to create user' };
      }
    } catch (error) {
      console.error('Error creating user:', error);
      
      // Handle validation errors
      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Failed to create user',
          errors: error.response.data.errors || {},
          error: error.response.data.message || error.message || 'Failed to create user'
        };
      }
      
      return {
        success: false,
        error: error.message || 'Failed to create user'
      };
    }
  },

  // Update user
  updateUser: async (userId, userData) => {
    try {
      const response = await apiClient.put(`/api/admin/users/${userId}/`, userData);
      
      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to update user' };
      }
    } catch (error) {
      console.error('Error updating user:', error);
      
      // Handle validation errors
      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Failed to update user',
          errors: error.response.data.errors || {},
          error: error.response.data.message || error.message || 'Failed to update user'
        };
      }
      
      return {
        success: false,
        error: error.message || 'Failed to update user'
      };
    }
  },

  // Patch user (partial update)
  patchUser: async (userId, userData) => {
    try {
      const response = await apiClient.patch(`/api/admin/users/${userId}/`, userData);
      
      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to update user' };
      }
    } catch (error) {
      console.error('Error updating user:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update user'
      };
    }
  },

  // Delete user
  deleteUser: async (userId) => {
    try {
      await apiClient.delete(`/api/admin/users/${userId}/`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete user'
      };
    }
  },

  // Activate/Deactivate user
  toggleUserStatus: async (userId, status) => {
    try {
      const response = await apiClient.patch(`/api/admin/users/${userId}/`, { status });
      
      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to update user status' };
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update user status'
      };
    }
  },

  // Get dashboard overview
  getDashboardOverview: async () => {
    try {
      const response = await apiClient.get('/api/admin/dashboard/overview/');
      
      if (response.data?.success) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: response.data?.message || 'No dashboard data received' };
      }
    } catch (error) {
      console.error('Error fetching dashboard overview:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to fetch dashboard data'
      };
    }
  },

  // Get registration trends
  getRegistrationTrends: async (period = 'weekly', startDate = null, endDate = null) => {
    try {
      const params = { period };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const queryString = new URLSearchParams(params).toString();
      const response = await apiClient.get(`/api/admin/dashboard/registration-trends/?${queryString}`);
      
      if (response.data?.success) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: response.data?.message || 'No trends data received' };
      }
    } catch (error) {
      console.error('Error fetching registration trends:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to fetch trends data'
      };
    }
  },

  // Get recent activities
  getRecentActivities: async (limit = 10, offset = 0) => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });

      const response = await apiClient.get(`/api/admin/dashboard/recent-activities/?${params.toString()}`);
      
      if (response.data?.success) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: response.data?.message || 'No activities data received' };
      }
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to fetch activities data'
      };
    }
  },

  // Get user statistics
  getUserStats: async () => {
    try {
      const response = await apiClient.get('/api/admin/dashboard/user-stats/');
      
      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'No user stats data received' };
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch user stats'
      };
    }
  },

  // Portal Configs API methods
  getPortalConfigs: async (params = {}) => {
    try {
      const {
        page = 1,
        page_size = 20,
        search = '',
        status = '',
        distributor = '',
        operator = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        page_size: page_size.toString()
      });

      // Add optional search and filter parameters
      if (search) queryParams.append('search', search);
      if (status) queryParams.append('status', status);
      if (distributor) queryParams.append('distributor', distributor);
      if (operator) queryParams.append('operator', operator);

      const response = await apiClient.get(`/api/admin/portal-configs/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            results: response.data.results,
            totalPages: Math.ceil(response.data.count / page_size),
            currentPage: page,
            pageSize: page_size
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching portal configs:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch portal configs'
      };
    }
  },

  createPortalConfig: async (configData) => {
    try {
      const response = await apiClient.post('/api/admin/portal-configs/', configData);
      
      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to create portal config' };
      }
    } catch (error) {
      console.error('Error creating portal config:', error);
      
      // Handle validation errors
      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Failed to create portal config',
          errors: error.response.data.errors || {},
          error: error.response.data.message || error.message || 'Failed to create portal config'
        };
      }
      
      return {
        success: false,
        error: error.message || 'Failed to create portal config'
      };
    }
  },

  updatePortalConfig: async (configId, configData) => {
    try {
      const response = await apiClient.put(`/api/admin/portal-configs/${configId}/`, configData);
      
      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to update portal config' };
      }
    } catch (error) {
      console.error('Error updating portal config:', error);
      
      // Handle validation errors
      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Failed to update portal config',
          errors: error.response.data.errors || {},
          error: error.response.data.message || error.message || 'Failed to update portal config'
        };
      }
      
      return {
        success: false,
        error: error.message || 'Failed to update portal config'
      };
    }
  },

  deletePortalConfig: async (configId) => {
    try {
      await apiClient.delete(`/api/admin/portal-configs/${configId}/`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting portal config:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete portal config'
      };
    }
  },

  // UPI Configs API methods
  getUpiConfigs: async (params = {}) => {
    try {
      const {
        page = 1,
        page_size = 20,
        search = '',
        status = '',
        distributor = '',
        operator = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        page_size: page_size.toString()
      });

      // Add optional search and filter parameters
      if (search) queryParams.append('search', search);
      if (status) queryParams.append('status', status);
      if (distributor) queryParams.append('distributor', distributor);
      if (operator) queryParams.append('operator', operator);

      const response = await apiClient.get(`/api/admin/upi-configs/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            results: response.data.results,
            totalPages: Math.ceil(response.data.count / page_size),
            currentPage: page,
            pageSize: page_size
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching UPI configs:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch UPI configs'
      };
    }
  },

  createUpiConfig: async (configData) => {
    try {
      const response = await apiClient.post('/api/admin/upi-configs/', configData);
      
      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to create UPI config' };
      }
    } catch (error) {
      console.error('Error creating UPI config:', error);
      
      // Handle validation errors
      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Failed to create UPI config',
          errors: error.response.data.errors || {},
          error: error.response.data.message || error.message || 'Failed to create UPI config'
        };
      }
      
      return {
        success: false,
        error: error.message || 'Failed to create UPI config'
      };
    }
  },

  updateUpiConfig: async (configId, configData) => {
    try {
      const response = await apiClient.put(`/api/admin/upi-configs/${configId}/`, configData);
      
      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to update UPI config' };
      }
    } catch (error) {
      console.error('Error updating UPI config:', error);
      
      // Handle validation errors
      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Failed to update UPI config',
          errors: error.response.data.errors || {},
          error: error.response.data.message || error.message || 'Failed to update UPI config'
        };
      }
      
      return {
        success: false,
        error: error.message || 'Failed to update UPI config'
      };
    }
  },

  deleteUpiConfig: async (configId) => {
    try {
      await apiClient.delete(`/api/admin/upi-configs/${configId}/`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting UPI config:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete UPI config'
      };
    }
  },

  // ============ PLAN MANAGEMENT ============

  // Get all plans with pagination and filters
  getPlans: async (params = {}) => {
    try {
      const {
        page = 1,
        page_size = 20,
        search = '',
        status = '',
        billing_cycle = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        page_size: page_size.toString()
      });

      if (search) queryParams.append('search', search);
      if (status) queryParams.append('status', status);
      if (billing_cycle) queryParams.append('billing_cycle', billing_cycle);

      const response = await apiClient.get(`/api/admin/plans/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            results: response.data.results,
            totalPages: Math.ceil(response.data.count / page_size),
            currentPage: page,
            pageSize: page_size
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch plans'
      };
    }
  },

  // Get single plan details
  getPlanById: async (planId) => {
    try {
      const response = await apiClient.get(`/api/admin/plans/${planId}/`);

      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Plan not found' };
      }
    } catch (error) {
      console.error('Error fetching plan:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch plan'
      };
    }
  },

  // Create new plan
  createPlan: async (planData) => {
    try {
      const response = await apiClient.post('/api/admin/plans/', planData);

      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to create plan' };
      }
    } catch (error) {
      console.error('Error creating plan:', error);

      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Failed to create plan',
          errors: error.response.data.errors || error.response.data,
          error: error.response.data.message || error.message || 'Failed to create plan'
        };
      }

      return {
        success: false,
        error: error.message || 'Failed to create plan'
      };
    }
  },

  // Update plan
  updatePlan: async (planId, planData) => {
    try {
      const response = await apiClient.put(`/api/admin/plans/${planId}/`, planData);

      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to update plan' };
      }
    } catch (error) {
      console.error('Error updating plan:', error);

      if (error.response?.data) {
        return {
          success: false,
          message: error.response.data.message || 'Failed to update plan',
          errors: error.response.data.errors || error.response.data,
          error: error.response.data.message || error.message || 'Failed to update plan'
        };
      }

      return {
        success: false,
        error: error.message || 'Failed to update plan'
      };
    }
  },

  // Delete plan (soft delete)
  deletePlan: async (planId) => {
    try {
      const response = await apiClient.delete(`/api/admin/plans/${planId}/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error deleting plan:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to delete plan'
      };
    }
  },

  // Toggle plan status
  togglePlanStatus: async (planId, status) => {
    try {
      const response = await apiClient.patch(`/api/admin/plans/${planId}/status/`, { status });

      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to update plan status' };
      }
    } catch (error) {
      console.error('Error toggling plan status:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update plan status'
      };
    }
  },

  // ============ SUBSCRIPTION MANAGEMENT ============

  // Get all subscriptions with pagination and filters
  getSubscriptions: async (params = {}) => {
    try {
      const {
        page = 1,
        page_size = 20,
        search = '',
        status = '',
        user_id = '',
        plan_id = '',
        expiring_soon = false,
        start_date = '',
        end_date = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        page_size: page_size.toString()
      });

      if (search) queryParams.append('search', search);
      if (status) queryParams.append('status', status);
      if (user_id) queryParams.append('user_id', user_id);
      if (plan_id) queryParams.append('plan_id', plan_id);
      if (expiring_soon) queryParams.append('expiring_soon', 'true');
      if (start_date) queryParams.append('start_date', start_date);
      if (end_date) queryParams.append('end_date', end_date);

      const response = await apiClient.get(`/api/admin/subscriptions/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            results: response.data.results,
            totalPages: Math.ceil(response.data.count / page_size),
            currentPage: page,
            pageSize: page_size
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch subscriptions'
      };
    }
  },

  // Get single subscription details
  getSubscriptionById: async (subscriptionId) => {
    try {
      const response = await apiClient.get(`/api/admin/subscriptions/${subscriptionId}/`);

      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Subscription not found' };
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch subscription'
      };
    }
  },

  // Activate subscription
  activateSubscription: async (subscriptionId, dates = {}) => {
    try {
      const response = await apiClient.post(`/api/admin/subscriptions/${subscriptionId}/activate/`, dates);

      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to activate subscription' };
      }
    } catch (error) {
      console.error('Error activating subscription:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to activate subscription'
      };
    }
  },

  // Cancel subscription
  cancelSubscription: async (subscriptionId) => {
    try {
      const response = await apiClient.post(`/api/admin/subscriptions/${subscriptionId}/cancel/`);

      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to cancel subscription' };
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to cancel subscription'
      };
    }
  },

  // Expire subscription
  expireSubscription: async (subscriptionId) => {
    try {
      const response = await apiClient.post(`/api/admin/subscriptions/${subscriptionId}/expire/`);

      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to expire subscription' };
      }
    } catch (error) {
      console.error('Error expiring subscription:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to expire subscription'
      };
    }
  },

  // Extend subscription
  extendSubscription: async (subscriptionId, days) => {
    try {
      const response = await apiClient.post(`/api/admin/subscriptions/${subscriptionId}/extend/`, { days });

      if (response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: 'Failed to extend subscription' };
      }
    } catch (error) {
      console.error('Error extending subscription:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to extend subscription'
      };
    }
  }

};

export default adminService;